/**
 * El diagnóstico de un golpe del panel de Word: los datos que da el motor y las frases que se pintan.
 * Sin React y sin red, para poder probarlo solo.
 */

export type Pieza = { id: string; titulo: string; hecha: boolean; gratis?: boolean };
export type Trabajo = {
  id: string; estado: "en_curso" | "hecho" | "fallido" | "interrumpido"; titulo: string;
  progreso: { hecho: number; total: number; fase?: string } | null; pasos: { titulo: string; estado: string }[] | null;
  error: string | null; resultado: { diagnostico: string | null; plan: string | null; hechas: string[]; sin_leer?: number } | null;
  inicio: string; fin: string | null;
};
export type Estimacion = {
  piezas: { id: string; titulo: string; llamadas: number; palabras: number }[];
  /** Las llamadas al modelo que se van a hacer de verdad, y cuántas veces viaja el libro entero. */
  llamadas: number; palabras: number; lecturas: number; minutos: number;
  /** Solo si el motor tiene de dónde sacarlo: lo que costó la última vez en este libro, o el precio del modelo. */
  dinero: { usd: number; base: "historial" | "precios" | "mixta" } | null;
};
/**
 * QUÉ OBRA ES (13-oct-2026): con qué tipo y género se lee este libro, si lo ha dicho el autor, y las dos listas para
 * elegirlo. Un motor anterior no lo manda: entonces el panel no enseña el selector.
 */
export type Obra = { elegida: boolean; tipo: string; lleva_genero: boolean; genero: { id: string; nombre: string; descripcion: string | null } | null; en_palabras: string;
  tipos: { id: string; nombre: string; lleva_genero: boolean }[]; generos: { id: string; nombre: string }[] };
export type Motivo = "sin_carpeta" | "sin_documento" | "es_generado" | "corto" | "no_leido";
export type Diagnostico = {
  hay: true; fichero: string; palabras: number; llave: boolean;
  /** El documento abierto es el manuscrito por escenas: el plan que salga de aquí no se podrá ejecutar. */
  por_escenas?: boolean;
  /** Cuántos informes son: lo dice el motor, que es quien los hace. */
  numero_de_piezas: number;
  /** Lo último que se hizo es de OTRO texto: el documento ha cambiado desde entonces. */
  texto_cambiado: boolean;
  /** Las piezas, en su orden, y si están hechas PARA EL TEXTO DE AHORA. `gratis`: el Word que las junta. */
  piezas: Pieza[];
  /** Lo que se pediría ahora (lo que gasta). */
  pedidos: string[];
  estimacion: Estimacion | null;
  /** Otro trabajo de este libro que impide pedirlo ahora, dicho por el motor. */
  ocupado: string | null;
  resultados: { diagnostico: string | null; plan: string | null; de_este_texto: boolean; leido: string | null; piezas: string[] };
  /** Qué se aprovecha al pedirlo otra vez con el mismo texto. El plan, nada: `entero`. */
  reanuda?: { lectura: string; informes: string; plan: string };
  /** El MISMO texto guardado con otro nombre («Guardar como»): el nombre con el que se hizo lo que hay. */
  mismo_texto_de?: string | null;
  /** Ya hay un plan de reescritura de este libro: pedir otro lo guarda aparte y escribe uno nuevo sin aprobar. */
  plan_vigente?: { aprobado: boolean } | null;
  /** Con qué tipo y género se leería ahora. */
  obra?: Obra;
  /** Lo que hay hecho es de este mismo texto, pero se leyó como OTRA obra (se cambió el tipo o el género). */
  obra_cambiada?: { antes: string } | null;
  trabajo: Trabajo | null;
};
export type SinDiagnostico = { hay: false; motivo: Motivo; fichero: string | null; palabras?: number; llave: boolean; numero_de_piezas: number; trabajo: Trabajo | null };

/**
 * EL AVISO DE ANTES DE EMPEZAR (Rais, 12-oct-2026): se dice siempre, salga lo que salga en la cuenta. La cuenta
 * del tiempo es un cálculo y nunca un techo.
 */
export const AVISO_DE_DURACION = "Son seis informes y puede tardar una hora o más. Mientras tanto puedes seguir trabajando en Word; no cierres el ordenador.";
/** Lo mismo, mientras trabaja. */
export const AVISO_EN_MARCHA = "Puede tardar una hora o más. Puedes seguir trabajando en Word mientras tanto; no cierres el ordenador.";

export const miles = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
export const dinero = (x: number) => { const c = Math.round(100 * (Number(x) || 0)); return `${miles(Math.floor(c / 100))},${String(c % 100).padStart(2, "0")} $`; };
const veces = (n: number, uno: string, varios: string) => `${miles(n)} ${n === 1 ? uno : varios}`;
const EN_LETRA = ["cero", "un", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez"];
/** Un número pequeño, como se dice: «seis informes». */
export const enLetra = (n: number) => EN_LETRA[n] ?? String(n);
/** «a, b y c», como se dice una lista (y «e» delante de «i»: «informe de lectura e informe del editor»). */
export const enLista = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} ${/^h?i(?!e)/i.test(xs[xs.length - 1]) ? "e" : "y"} ${xs[xs.length - 1]}`);
const enMinuscula = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
export function diaYHora(iso: string | null | undefined) {
  const d = new Date(iso ?? "");
  if (!iso || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export const enMarcha = (t: Trabajo | null | undefined) => t?.estado === "en_curso";
const cortado = (t: Trabajo | null | undefined) => t?.estado === "fallido" || t?.estado === "interrumpido";
/** Los informes (lo que gasta), sin el Word que los junta. */
const informes = (d: Diagnostico) => d.piezas.filter((p) => !p.gratis);

/** ¿Se quedó a medias? Hay algo por pedir y, o el último intento se cortó, o ya hay informes hechos de este texto. */
export function quedoAMedias(d: Diagnostico): boolean {
  if (!d.pedidos.length) return false;
  return cortado(d.trabajo) || (!d.texto_cambiado && informes(d).some((p) => p.hecha));
}

/**
 * Qué enseña la pestaña ahora:
 *   · `en_curso`: hay un diagnóstico trabajando;
 *   · `freno`: con este documento no se puede (sin guardar, lo escribió HumanInk, muy corto, no se pudo leer);
 *   · `estimado`: el primer clic está dado: se enseña qué se va a hacer y lo que cuesta, y se espera el «sí»;
 *   · `a_medias`: el último intento no terminó, o falta parte;
 *   · `hecho`: hay un diagnóstico que abrir;
 *   · `nada`: todavía no se ha pedido.
 */
export type Momento = "en_curso" | "freno" | "estimado" | "a_medias" | "hecho" | "nada";
export function momentoDe(d: Diagnostico | SinDiagnostico, pedido: boolean): Momento {
  if (enMarcha(d.trabajo)) return "en_curso";
  if (!d.hay) return "freno";
  if (pedido) return "estimado";
  if (quedoAMedias(d)) return "a_medias";
  if (d.resultados.diagnostico) return "hecho";
  return "nada";
}

/**
 * UN SOLO BOTÓN RELLENO A LA VEZ (Rais, 12-oct-2026): el del paso que toca. Los demás, sin relleno.
 * `pedir`: el primer clic. `confirmar`: el «sí», que es el que gasta. `abrir`: leer el diagnóstico ya hecho.
 */
export function botonQueToca(m: Momento): "pedir" | "confirmar" | "abrir" | null {
  return m === "estimado" ? "confirmar" : m === "hecho" ? "abrir" : m === "nada" || m === "a_medias" ? "pedir" : null;
}

/** Qué es, dicho antes de pedir nada. Cuántos informes y cuáles lo dice el motor, que es quien los hace. */
export function fraseDeQueEs(d: Diagnostico): string {
  const cuantos = enLetra(d.numero_de_piezas);
  return `${cuantos.charAt(0).toUpperCase()}${cuantos.slice(1)} informes de tu manuscrito, uno detrás de otro, y un Word que los junta: `
    + `${enLista(informes(d).map((p) => enMinuscula(p.titulo)))}. Se hace con el texto tal como está guardado, y tu manuscrito no se toca. `
    + "Antes de empezar te digo qué se va a hacer y cuánto cuesta.";
}

/** Con este documento no se puede hacer el diagnóstico: por qué, y qué hacer. */
export function porQueNo(d: SinDiagnostico): string {
  switch (d.motivo) {
    case "es_generado": return `«${d.fichero ?? "Este documento"}» lo ha escrito HumanInk (un informe, un diagnóstico o un plan). Abre tu manuscrito en Word y vuelve a esta pestaña: el diagnóstico se hace del manuscrito.`;
    case "corto": {
      // La novela construida con pocas escenas (Rais, 14-oct: 485 palabras, 1 de 90 escenas; leyó «no puede hacer el informe»).
      const n = /novela \((\d+) de (\d+) escenas?\)/.exec(d.fichero ?? "");
      const porque = n ? ` Es tu novela construida con ${n[1]} de ${n[2]} escenas: cierra más escenas en la pestaña Escritura y vuelve a construirla.` : "";
      return `Para el diagnóstico hace falta un manuscrito de 500 palabras o más${typeof d.palabras === "number" ? `; este tiene ${miles(d.palabras)}` : ""}.${porque}`;
    }
    case "no_leido": return `No he podido leer «${d.fichero ?? "el documento"}». Guárdalo en Word y prueba otra vez.`;
    default: return "Guarda el documento en este ordenador (un .docx) y vuelve a esta pestaña: el diagnóstico se hace con el texto guardado.";
  }
}

/**
 * EL MANUSCRITO POR ESCENAS: el diagnóstico se puede hacer, pero su plan de reescritura no se podrá ejecutar, porque la
 * reescritura trabaja sobre la novela construida. Se dice ANTES de gastar (Rais lo pagó sin saberlo el 14-oct). No frena.
 */
export const AVISO_POR_ESCENAS = "Este es el manuscrito por escenas. El diagnóstico se puede hacer, pero el plan de reescritura que salga de él no se podrá ejecutar: la reescritura trabaja sobre la novela construida. Si lo que quieres es reescribir, construye antes la novela (pestaña Escritura) y pide el diagnóstico con ella abierta.";
export const avisoDePorEscenas = (d: Diagnostico): string | null => (d.por_escenas === true ? AVISO_POR_ESCENAS : null);

/** Por qué AHORA no se puede pedir, o `null` si se puede. Lo diría el motor al intentarlo; aquí se dice antes. */
export function frenoDe(d: Diagnostico): string | null {
  if (d.ocupado) return d.ocupado;
  if (d.pedidos.length > 0 && !d.llave) return "Falta tu llave de OpenRouter: ponla en Ajustes. Los informes se pagan de tu llave.";
  return null;
}

/** El rótulo del primer clic, que solo enseña la cuenta. */
export function rotuloDePedir(d: Diagnostico): string {
  if (quedoAMedias(d) && !d.texto_cambiado) return "Seguir donde se quedó";
  if (d.resultados.diagnostico && d.mismo_texto_de) return d.resultados.plan ? "Hacer otro para este fichero" : "Hacerlo entero para este fichero";
  if (d.resultados.diagnostico && d.texto_cambiado) return "Hacer otro con el texto de ahora";
  return "Hacer el diagnóstico";
}
/** El rótulo del segundo clic, que es el único que gasta. */
export const rotuloDeConfirmar = (d: Diagnostico) => (quedoAMedias(d) && !d.texto_cambiado ? "Sí, seguir con el diagnóstico" : "Sí, hacer el diagnóstico");

/** Cuánto es un rato largo, dicho sin falsa precisión. */
function duracion(minutos: number): string {
  if (minutos < 60) return `unos ${minutos} minutos`;
  const h = Math.floor(minutos / 60), m = Math.round((minutos % 60) / 10) * 10;
  const horas = m === 60 ? h + 1 : h, resto = m === 60 ? 0 : m;
  return `alrededor de ${horas === 1 ? "una hora" : `${horas} horas`}${resto ? ` y ${resto} minutos` : ""}`;
}
/** El tiempo calculado para ESTE libro. Es un cálculo, no un techo: siempre se dice que puede alargarse. */
export function fraseDelTiempo(e: Estimacion | null): string {
  if (!e || !e.minutos) return "";
  return e.minutos < 60
    ? `Por el tamaño de tu libro, ${duracion(e.minutos)}; puede alargarse hasta una hora o más.`
    : `Por el tamaño de tu libro, ${duracion(e.minutos)}; puede alargarse.`;
}

/** Cuando no hay nada que pedir al modelo: solo se vuelve a montar el Word con lo que ya está hecho. */
export const FRASE_SIN_GASTO = "Los informes de este texto ya están hechos y no se repiten: solo se vuelve a montar el Word del diagnóstico. No cuesta nada.";

/** Lo que va a viajar, dicho antes de gastar. */
export function fraseDeLoQueViaja(e: Estimacion | null): string {
  if (!e || !e.llamadas) return "";
  return `Tu libro se lee entero ${veces(e.lecturas, "vez", "veces")} (${miles(e.palabras)} palabras en total), en ${veces(e.llamadas, "llamada", "llamadas")} al modelo.`;
}

/** Lo que va a costar, si el motor tiene con qué decirlo; si no, se dice que no. Nunca una cifra sin base. */
export function fraseDelDinero(e: Estimacion | null): string {
  if (!e || !e.dinero) return "Se paga de tu llave de OpenRouter; no tengo precios para estimarlo.";
  const base = e.dinero.base === "historial" ? "es lo que costó la última vez en este libro"
    : e.dinero.base === "precios" ? "calculado con el precio del modelo" : "calculado con lo que costó la última vez y con el precio del modelo";
  return `Unos ${dinero(e.dinero.usd)} de tu llave de OpenRouter (${base}). Es una estimación: lo que cueste de verdad depende de cuánto escriba el modelo.`;
}

/** Los informes, en su orden, con lo que se dice de cada uno antes de empezar: se hace ahora, o ya está hecho. */
export function listaDeInformes(d: Diagnostico): { id: string; titulo: string; hecho: boolean }[] {
  return informes(d).map((p) => ({ id: p.id, titulo: p.titulo, hecho: !d.pedidos.includes(p.id) }));
}

/** Por dónde va el diagnóstico en marcha: «Paso 2 de 7 · Informe del editor». */
export function fraseDeAvance(t: Trabajo): string {
  const total = t.pasos?.length ?? t.progreso?.total ?? 0;
  if (!total) return "Empezando…";
  const hecho = Math.min(t.progreso?.hecho ?? 0, total - 1);
  const que = t.progreso?.fase || t.pasos?.[hecho]?.titulo || "";
  return `Paso ${hecho + 1} de ${total}${que ? ` · ${que}` : ""}`;
}
export const avanceDe = (t: Trabajo) => { const total = t.pasos?.length ?? t.progreso?.total ?? 0; return total ? Math.min(1, (t.progreso?.hecho ?? 0) / total) : 0; };

/** Lo que hay hecho, dicho al terminar (o al volver otro día). */
export function fraseDeHecho(d: Diagnostico): string {
  const hechos = d.piezas.filter((p) => !p.gratis && d.resultados.piezas.includes(p.id)).map((p) => enMinuscula(p.titulo));
  const cuando = diaYHora(d.resultados.leido);
  const sinLeer = d.trabajo?.estado === "hecho" ? d.trabajo.resultado?.sin_leer ?? 0 : 0;
  return `Hecho${cuando ? ` con el texto guardado el ${cuando}` : ""}: ${enLista(hechos)}. Los documentos están en la carpeta del libro, junto a tu manuscrito.`
    + (sinLeer ? ` El informe de lectura ha salido con ${veces(sinLeer, "parte", "partes")} del libro sin leer, y lo dice dentro.` : "");
}
/** Con un diagnóstico hecho y el manuscrito cambiado después: se dice, porque habla de otro texto. */
export const FRASE_DE_TEXTO_CAMBIADO = "Desde entonces has guardado cambios en el manuscrito: este diagnóstico habla del texto de antes.";

/**
 * «GUARDAR COMO» (equipo rojo, 12-oct-2026): el documento tiene el mismo texto que otro del que ya hay diagnóstico (o
 * medio). No ha cambiado nada, y se dice así: decir «has guardado cambios» llevaría a pagar seis informes del mismo texto.
 */
export function fraseDeMismoTexto(d: Diagnostico): string {
  if (!d.mismo_texto_de) return "";
  return aquelTermino(d)
    ? `Este documento tiene el mismo texto que «${d.mismo_texto_de}», con otro nombre: el diagnóstico que ya tienes te vale.`
    : `Empezaste el diagnóstico de este mismo texto cuando el fichero se llamaba «${d.mismo_texto_de}», y no terminó. Con el nombre de ahora se hace entero otra vez; si le devuelves aquel nombre, sigue donde se quedó.`;
}
/**
 * ¿Terminó el diagnóstico que se hizo con el otro nombre? El plan es lo último: con el Word del diagnóstico hecho y el
 * plan sin hacer (se cortó), aquel NO terminó, y decir «te vale» llevaría a pagar seis informes aquí en vez de un plan
 * allí (equipo rojo, 17.ª vuelta).
 */
const aquelTermino = (d: Diagnostico) => !!d.resultados.diagnostico && !!d.resultados.plan;
/** Lo mismo, dicho en la cuenta de antes de gastar. */
export const avisoDeMismoTexto = (d: Diagnostico) => (d.mismo_texto_de
  ? `Es el mismo texto que «${d.mismo_texto_de}», del que ya ${aquelTermino(d) ? "tienes" : "empezaste"} el diagnóstico: los informes se hacen y se pagan otra vez.` : "");

/** El plan que ya hay: pedir otro no lo borra, pero deja de ser el vigente y el nuevo nace sin aprobar. Se dice antes. */
export function fraseDelPlanVigente(d: Diagnostico): string {
  if (!d.plan_vigente || !d.pedidos.includes("plan")) return "";
  return `Ya hay un plan de reescritura de este libro${d.plan_vigente.aprobado ? ", y lo habías aprobado" : ""}: no se borra, se guarda aparte, y se escribe uno nuevo sin aprobar.`;
}

/** Lo que pasó con el intento que no terminó: en qué paso se quedó, por qué, y qué pasa al pedirlo otra vez. */
export function fraseDeAMedias(d: Diagnostico): string {
  const t = d.trabajo;
  const i = t?.pasos?.findIndex((p) => p.estado === "fallido" || p.estado === "interrumpido") ?? -1;
  const donde = t?.pasos && i >= 0 ? `Se quedó en el paso ${i + 1} de ${t.pasos.length}. ` : "";
  const porQue = cortado(t) ? `${t?.error ?? "No ha terminado."} ` : "";
  const hechos = informes(d).filter((p) => p.hecha).map((p) => enMinuscula(p.titulo));
  // Solo se promete que no se paga otra vez si el texto es el mismo: con otro texto se rehace todo.
  const queda = d.texto_cambiado
    ? "Has guardado cambios en el manuscrito desde entonces: al pedirlo otra vez se hace entero, con el texto de ahora."
    : hechos.length ? `Lo que ya está hecho (${enLista(hechos)}) no se vuelve a pagar.`
    : "Si no has cambiado el texto, lo que ya se haya leído no se paga otra vez.";
  // El plan es una sola llamada larga: si se corta mientras se escribe, no hay nada que aprovechar. Lo dice el motor.
  const soloElPlan = d.pedidos.length === 1 && d.pedidos[0] === "plan";
  const plan = !d.texto_cambiado && soloElPlan && (d.reanuda?.plan ?? "entero") === "entero" ? " El plan de reescritura no se guarda a medias: se pide entero." : "";
  return `El diagnóstico no ha terminado. ${donde}${porQue}${queda}${plan}`;
}

// ── Qué obra es ──────────────────────────────────────────────────────────────────────────────────

/** La opción de no decir el género: se lee con la lente general, como antes de poder elegir. */
export const SIN_GENERO = "Sin decir (lectura general)";
/** Qué géneros se ofrecen para el tipo elegido: ninguno si ese tipo no lleva género (un ensayo). */
export const llevaGenero = (o: Obra, tipo: string): boolean => o.tipos.find((t) => t.id === tipo)?.lleva_genero ?? false;
/** La línea que dice con qué lente se va a leer; sin género, dice qué se gana diciéndolo. */
export function fraseDeLaObra(o: Obra): string {
  if (o.lleva_genero && !o.genero) return "Se leerá con la lente general. Si dices el género, el diagnóstico mira lo que ese género pide y no lo que no le toca.";
  return `Se leerá como «${o.en_palabras}».`;
}
/** Lo hecho es de este texto, pero leído como otra obra: se dice antes de que el autor pida —y pague— otro entero. */
export function fraseDeObraCambiada(d: Diagnostico): string {
  if (!d.obra_cambiada) return "";
  return `El diagnóstico que tienes se hizo como «${d.obra_cambiada.antes}». Si lo pides ahora se hace entero otra vez, leído como «${d.obra?.en_palabras ?? "lo que has elegido"}»: los informes de antes no se borran.`;
}
